import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { lumi } from '@/lib/lumi'
import PhoneInput from '@/components/PhoneInput'
import {GraduationCap, ArrowRight, Loader2, AlertCircle} from 'lucide-react'

const EMAIL_FN_URL =
  'https://api.lumi.new/v1/functions/p427216140118241280/send-welcome-email'

function CapacitacaoEquipe() {
  const navigate = useNavigate()
  const [indicadoPor, setIndicadoPor] = useState('monica')

  // Formulário
  const [nome, setNome] = useState('')
  const [whatsapp, setWhatsapp] = useState('')
  const [email, setEmail] = useState('')

  // Estado
  const [enviando, setEnviando] = useState(false)
  const [erro, setErro] = useState('')

  useEffect(() => {
    const params = new URLSearchParams(window.location.search)
    const indicado = params.get('indicado_por')?.trim()
    if (indicado) {
      setIndicadoPor(indicado)
    }
    window.scrollTo(0, 0)
  }, [])

  async function handleSubmit(e: React.FormEvent) {
    e.preventDefault()
    setErro('')

    if (!nome.trim() || !whatsapp.trim() || !email.trim()) {
      setErro('Preencha todos os campos para continuar.')
      return
    }

    setEnviando(true)
    try {
      const agora = new Date().toISOString()
      const registro = {
        nome: nome.trim(),
        whatsapp: whatsapp.trim(),
        email: email.trim(),
        origem: 'Capacitacao Equipe',
        indicadoPor,
        statusInscricao: 'Inscrita',
        etapaAtual: 'inscricao',
        dataInscricao: agora,
        createdAt: agora,
        updatedAt: agora,
      }

      const novoRegistro = await lumi.entities.capacitacao.create(registro)

      // Salvar identificação na sessão para que /curso-aulas vincule
      // corretamente a conclusão do curso a este registro em `capacitacao`
      // (mesmo padrão usado em /curso/inscricao e /selecao).
      try {
        const id = (novoRegistro as any)?._id || (novoRegistro as any)?.id || ''
        if (id) sessionStorage.setItem('capacitacao_id', id)
        sessionStorage.setItem('capacitacao_whatsapp', whatsapp.trim())
        sessionStorage.setItem('capacitacao_nome', nome.trim())
        sessionStorage.setItem('capacitacao_email', email.trim())
      } catch {
        // ignore
      }

      // Disparar e-mail de boas-vindas
      void fetch(EMAIL_FN_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          tipo: 'capacitacao_equipe_acesso',
          nome: nome.trim(),
          email: email.trim(),
        }),
      }).catch(err => console.error('[capacitacao-equipe] erro ao enviar e-mail', err))

      // Redirecionar direto para o player do curso
      navigate('/curso-aulas')
    } catch (err) {
      console.error('[capacitacao-equipe] erro ao salvar', err)
      setErro('Não foi possível concluir seu cadastro. Tente novamente.')
    } finally {
      setEnviando(false)
    }
  }

  // ── Página principal ─────────────────────────────────────────────────────────
  return (
    <div className="min-h-screen bg-[#F8F4EF]">
      {/* Header */}
      <header className="bg-white border-b border-[#EDE0D4] sticky top-0 z-30">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 py-4">
          <p className="font-cormorant text-lg sm:text-xl font-semibold text-[#2D6A4F]">
            Diagnóstico Saúde e Emoções
          </p>
        </div>
      </header>

      {/* Hero */}
      <section className="px-4 sm:px-6 pt-12 sm:pt-20 pb-10 sm:pb-14">
        <div className="max-w-2xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 rounded-full bg-[#D8F3DC] text-[#2D6A4F] text-xs sm:text-sm font-medium mb-6">
            Capacitação da Equipe
          </span>
          <h1 className="font-cormorant text-4xl sm:text-6xl font-semibold text-[#2C2C2A] leading-[1.1] mb-5">
            Uma Gota de Equilíbrio
          </h1>
          <p className="text-base sm:text-lg text-[#5F5E5A] leading-relaxed">
            Acesse o curso introdutório de óleos essenciais e receba seu certificado.
          </p>
        </div>
      </section>

      {/* Formulário */}
      <section className="px-4 sm:px-6 pb-16 sm:pb-20">
        <div className="max-w-xl mx-auto">
          <div className="bg-white rounded-3xl p-6 sm:p-8 border border-[#EDE0D4] shadow-sm">
            <div className="flex items-center gap-3 mb-6">
              <GraduationCap className="text-[#52B788] shrink-0" size={24} />
              <p className="text-[#2C2C2A] text-sm sm:text-base">
                Preencha seus dados para acessar as aulas.
              </p>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5">
              {/* Nome */}
              <div>
                <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">
                  Nome completo <span className="text-red-500">*</span>
                </label>
                <input
                  type="text"
                  value={nome}
                  onChange={e => setNome(e.target.value)}
                  placeholder="Seu nome completo"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-[#EDE0D4] bg-[#F8F4EF] text-[#2C2C2A] placeholder-[#B08968]/60 focus:outline-none focus:border-[#52B788] focus:ring-1 focus:ring-[#52B788] transition-colors"
                />
              </div>

              {/* WhatsApp */}
              <div>
                <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">
                  WhatsApp <span className="text-red-500">*</span>
                </label>
                <PhoneInput
                  value={whatsapp}
                  onChange={setWhatsapp}
                  required
                />
              </div>

              {/* E-mail */}
              <div>
                <label className="block text-sm font-medium text-[#2C2C2A] mb-1.5">
                  E-mail <span className="text-red-500">*</span>
                </label>
                <input
                  type="email"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  placeholder="seu@email.com"
                  required
                  className="w-full px-4 py-3 rounded-xl border border-[#EDE0D4] bg-[#F8F4EF] text-[#2C2C2A] placeholder-[#B08968]/60 focus:outline-none focus:border-[#52B788] focus:ring-1 focus:ring-[#52B788] transition-colors"
                />
              </div>

              {/* Erro */}
              {erro && (
                <div className="flex items-start gap-2 bg-red-50 border border-red-200 rounded-xl px-4 py-3 text-sm text-red-700">
                  <AlertCircle size={16} className="shrink-0 mt-0.5" />
                  <span>{erro}</span>
                </div>
              )}

              {/* Botão */}
              <button
                type="submit"
                disabled={enviando}
                className="w-full flex items-center justify-center gap-2 bg-[#2D6A4F] text-white font-medium px-6 py-4 rounded-full hover:bg-[#1f4f3a] transition-colors disabled:opacity-60 disabled:cursor-not-allowed text-base"
              >
                {enviando ? (
                  <>
                    <Loader2 className="animate-spin" size={18} />
                    Processando…
                  </>
                ) : (
                  <>
                    Acessar o curso
                    <ArrowRight size={18} />
                  </>
                )}
              </button>
            </form>
          </div>
        </div>
      </section>

      <footer className="py-8 text-center text-xs sm:text-sm text-[#5F5E5A]">
        © {new Date().getFullYear()} Diagnóstico Saúde e Emoções · Mônica Maria
      </footer>
    </div>
  )
}

export default CapacitacaoEquipe
